import {FormikProps} from 'formik';
import {
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import {
  TextField,
  Button,
  LoadingOverlay,
} from '@finpay-development/shared-components';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import React, {useEffect, useState} from 'react';
import {Range, Slider} from './slider';
import {useDispatch, useSelector} from 'react-redux';
import {AppDispatch} from '../../../../../shared/state/store';
import {
  Scholarship
} from '../../../../../admin-configuration/models/scholarship';
import {showErrorStatus} from '../../../../../security/state/user-slice';
import {RootState} from '../../../../../shared/state/root-reducer';
import {
  admissionsAdvisorService
} from '../../../../../admissions-advisor/services/admissions-advisor-services';
import {patientService} from '../../../../../patient/services/patient-service';
import moment from 'moment';
import _ from 'lodash';
import {
  configGetScholarships
} from '../../../../../admin-configuration/state/admin-configuration-thunk';
import {
  setClientScholarshipConfig
} from '../../../../../admin-configuration/state/admin-configuration-slice';
import FileSaver from 'file-saver';

interface FinancialAssistanceProps {
  formik: FormikProps<any>
  setWEInitialValues: React.Dispatch<React.SetStateAction<{ clientId: number | null, householdSize: number, state: string, yearlySalary: number, monthlyExpenses: number }>>
}

interface ScholarshipConfig {
  config: Scholarship[]
  fplSlider: {
    sliderMin: number
    sliderMax: number
    ranges: Range[],
  }
  expenseIncomeSlider: {
    sliderMin: number
    sliderMax: number
    ranges: Range[],
  }
}

let scholarshipConfigCache: Record<number, ScholarshipConfig> = {}

export const FinancialAssistance = ({formik, setWEInitialValues}: FinancialAssistanceProps) => {

  /**
   * todo:
   * Find a better way to keep track of the selected client scholarship configuration.
   * Possibly combine all of them
   * (scholarshipConfigCache (local), selectedScholarshipConfig (formik), clientScholarshipsConfig (Redux))
   * */


  const {touched, errors} = formik

  const dispatch = useDispatch<AppDispatch>();
  const educationLevelMeta = useSelector((state: RootState) => {
    return state.metaData.educationLevel;
  });
  const clientWaiversMap = useSelector(
      (state: RootState) => {
        return state.patientContext.clientWaiversMap;
      },
  );
  const selectedPatientWaiver = useSelector(
      (state: RootState) => state.patientContext.selectedPatientWaiver
  );
  const [scholarshipConfig, setScholarshipConfig ] = useState<ScholarshipConfig>(scholarshipConfigCache[formik.values.clientId] || {})
  const [isLoading, setIsLoading] = useState(false);
  const [isDownloadButtonLoading, setIsDownloadButtonLoading] = useState(false);

  const clientWaiverConfig = clientWaiversMap.get(formik.values.clientId!)

  useEffect(()=>{

    const fetchDataAndSetState = async () => {

      if(!scholarshipConfigCache?.[formik.values.clientId]){

        const thunkResponse = await dispatch(configGetScholarships({ paramId: -2, clientId: formik.values.clientId })).unwrap()

        if (!thunkResponse) {
          return
        }

        if(thunkResponse.length ===0){
          dispatch(showErrorStatus("Scholarship config is not set up for this client"));
          return
        }

        let response = _.clone(thunkResponse)

        const clientScholarshipConfig = buildClientScholarshipConfigObj(response)

        scholarshipConfigCache = {
          ...scholarshipConfigCache,
          [formik.values.clientId]: clientScholarshipConfig
        }

        setScholarshipConfig(clientScholarshipConfig)

      }else{
        setScholarshipConfig(scholarshipConfigCache?.[formik.values.clientId])
        dispatch(setClientScholarshipConfig(scholarshipConfigCache[formik.values.clientId].config))
      }

    }

    fetchDataAndSetState()

  }, [formik.values.clientId])


  useEffect(()=>{

    //todo: simplify below condition
    if(
        formik.values.patientId &&
        !formik.values.selectedScholarshipConfig &&
        Array.isArray(scholarshipConfig?.config) &&
        selectedPatientWaiver.scholarshipPercentage >=0 &&
        (typeof selectedPatientWaiver.fplRatio === 'number' && selectedPatientWaiver.fplRatio > 0) &&
        (typeof selectedPatientWaiver.expenseIncomeRatio ==='number' && selectedPatientWaiver.expenseIncomeRatio> 0)
    ){
      const selectedConfig = scholarshipConfig.config.find((config) => {
        return config?.scholarshipPercentage === formik.values.scholarshipPercentage
      })
      if(selectedConfig) formik.setFieldValue('selectedScholarshipConfig', selectedConfig)
    }

  }, [formik.values.patientId, scholarshipConfig?.fplSlider?.sliderMin, formik.values.scholarshipPercentage])


  return(
      <>
        {
          Array.isArray(scholarshipConfig?.config) && scholarshipConfig?.config?.length > 0 ? (
              <Grid container spacing={2}>
                <Grid item xs={3}>
                  <TextField
                      name="monthlyExpenses"
                      label="Monthly Expenses"
                      type="number"
                      value={formik.values.monthlyExpenses || ""}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      startAdornment={<AttachMoneyIcon/>}
                      test-id="patient-tab-monthly-expenses"
                      className={`${touched.monthlyExpenses && Boolean(errors.monthlyExpenses) ? 'error-border' : ""}`}
                      helperText={touched.monthlyExpenses && errors.monthlyExpenses ? String(errors.monthlyExpenses) : undefined}
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                      name="householdSize"
                      label="Household Size"
                      type="number"
                      value={formik.values.householdSize || ""}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className={`${touched.householdSize && Boolean(errors.householdSize) ? 'error-border' : ""}`}
                      helperText={touched.householdSize && errors.householdSize ? String(errors.householdSize) : undefined}
                  />
                </Grid>
                <Grid item xs={6} className="MuiTextField-root">
                  <FormControl fullWidth size="small">
                    <InputLabel id="highest-education">Highest Education</InputLabel>
                    <Select
                        labelId="education-level"
                        test-id="patient-tab-highest-education"
                        label="Highest Education"
                        value={formik.values.educationLevel}
                        onChange={(e) => {
                          let educationLevelId = (e.target as HTMLInputElement).value;
                          if (educationLevelId) {
                            formik.setFieldValue('educationLevel', educationLevelId);
                          }
                        }}
                        style={{
                          width: '100%',
                        }}
                        renderValue={(selected) => {
                          let selectedEducationLevel = educationLevelMeta.find((lvl) => lvl.educationLevelId === selected)
                          return selectedEducationLevel?.educationLevel ||
                              'Select Education Level';

                        }}
                    >
                      {educationLevelMeta.
                          map((educationLvl) => {
                            return (
                                <MenuItem
                                    key={educationLvl.educationLevelId}
                                    value={educationLvl.educationLevelId}
                                    test-id={`patient-tab-financial-assistance-menu-${educationLvl.educationLevelId}`}
                                >
                                  {educationLvl.educationLevel}
                                </MenuItem>
                            );
                          })
                      }
                    </Select>
                  </FormControl>
                </Grid>
                <Grid
                    container
                    item
                    xs={12}
                    spacing={2}
                    sx={{
                      justifyContent: !formik.values.selectedScholarshipConfig ? 'flex-end' : null
                    }}
                >
                  {
                      formik.values.selectedScholarshipConfig && (
                          <Grid
                              container
                              item
                              xs={9}
                              justifyContent="center"
                              alignItems="center"
                              sx={{
                                backgroundColor: `${
                                  formik.values.selectedScholarshipConfig?.scholarshipPercentage === 0 ? '#FF0000' : 
                                      formik.values.selectedScholarshipConfig?.scholarshipPercentage === 100 ? "#3BB143" : 
                                      "#FFDB58"
                                }`,
                                backgroundClip: 'content-box',
                              }}
                          >
                            <Typography sx={{color: 'white'}} fontWeight="bold">
                              {
                                  formik.values.selectedScholarshipConfig?.scholarshipPercentage === 0 ?
                                      `Ineligible for scholarship (0%)` :
                                `Eligible for ${formik.values.selectedScholarshipConfig?.clientScholarshipLevelDesc } (${formik.values.selectedScholarshipConfig?.scholarshipPercentage}%)`}
                            </Typography>
                          </Grid>
                      )
                  }
                  <Grid item xs={3}>
                    <Button
                        fullWidth
                        disabled={isLoading}
                        test-id={"patient-tab-run-eligibility-btn"}
                        onClick={async()=>{

                          setIsLoading(true);

                          //todo : use Yup validation
                          if (!formik.values.householdSize ||
                              formik.values.state === "-1" ||
                              !formik.values.yearlySalary ||
                              !formik.values.monthlyExpenses
                          ) {
                            let errorMsg = 'Please resolve the following issues: ';
                            if (!formik.values.householdSize) errorMsg += 'Household Size is required; ';
                            if (formik.values.state === "-1") errorMsg += 'State is required; ';
                            if (!formik.values.yearlySalary) errorMsg += 'Yearly Salary is required; '
                            if (!formik.values.monthlyExpenses) errorMsg += 'Monthly Expenses is required; '
                            dispatch(showErrorStatus(errorMsg));
                            setIsLoading(false);
                            return;
                          }

                          if(formik.values.householdSize > 8 || formik.values.householdSize < 1){
                            dispatch(showErrorStatus('Household Size must be between 1 and 8'));
                            setIsLoading(false);
                            return;
                          }

                          if(formik.values.monthlyExpenses < 0){
                            dispatch(showErrorStatus('Monthly Expenses must be 0 or greater'));
                            setIsLoading(false);
                            return;
                          }

                          const response = await admissionsAdvisorService.getNationalPovertyThreshold(
                              -2,
                              formik.values.householdSize,
                              formik.values.state,
                              new Date().getFullYear(),
                          );

                          if (response.hasErrors) {
                            dispatch(showErrorStatus("Error retrieving National Poverty Guideline. Please reach out to a FinPay administrator. "))
                            setIsLoading(false)
                            return
                          }

                          const waiverResponse = await patientService.getPatientWaiverEligibility({
                            clientId: formik.values.clientId,
                            povertyThreshold: Number(response.entity.data.poverty_threshold),
                            monthlyIncome: formik.values.yearlySalary/12,
                            monthlyExpenses: formik.values.monthlyExpenses,
                          })

                          if (waiverResponse.hasErrors) {
                            dispatch(showErrorStatus("Error retrieving waiver eligibility. Please reach out to a FinPay administrator."))
                            setIsLoading(false)
                            return
                          }

                          formik.setFieldValue('fplRatio', waiverResponse.entity.fplRatio)
                          formik.setFieldValue('expenseIncomeRatio', waiverResponse.entity.expenseIncomeRatio)
                          formik.setFieldValue('scholarshipPercentage', waiverResponse.entity.scholarshipPercentage)
                          formik.setFieldValue('expirationDt', moment().add(clientWaiverConfig?.expireDays || 0, 'days').toISOString())

                          if(!waiverResponse.entity.selectedScholarshipConfig){
                            const selectedConfig = scholarshipConfig.config.find((config) => {
                              return config?.scholarshipPercentage == waiverResponse.entity?.scholarshipPercentage
                            })
                            formik.setFieldValue('selectedScholarshipConfig', selectedConfig)
                          }else{
                            formik.setFieldValue('selectedScholarshipConfig', waiverResponse.entity.selectedScholarshipConfig)
                          }

                          setIsLoading(false)

                          setWEInitialValues({
                            clientId: formik.values.clientId,
                            householdSize: formik.values.householdSize,
                            state: formik.values.state,
                            yearlySalary :formik.values.yearlySalary,
                            monthlyExpenses : formik.values.monthlyExpenses
                          })

                        }}
                    >
                      Run Eligibility
                    </Button>
                  </Grid>
                </Grid>
                <Grid
                    item
                    xs={12}
                    sx={{
                      backgroundColor: '#f5f7f9',
                      backgroundClip: 'content-box'
                    }}>
                  <Slider
                      title="% of Federal Poverty Level Based on Household Size"
                      min={scholarshipConfig?.fplSlider?.sliderMin}
                      max={scholarshipConfig?.fplSlider?.sliderMax}
                      value={formik.values.fplRatio}
                      ranges={scholarshipConfig?.fplSlider?.ranges}
                  />
                </Grid>
                <Grid
                    item
                    xs={12}
                    sx={{
                      backgroundColor: '#f5f7f9',
                      backgroundClip: 'content-box'
                    }}>
                  <Slider
                      title="Expense to Income Ratio"
                      min={scholarshipConfig?.expenseIncomeSlider?.sliderMin}
                      max={scholarshipConfig?.expenseIncomeSlider?.sliderMax}
                      value={formik.values.expenseIncomeRatio}
                      ranges={scholarshipConfig?.expenseIncomeSlider?.ranges}
                  />
                </Grid>
                <Grid
                    container
                    item
                    justifyContent={formik.values.waiverId > 0 ? "space-between" : "flex-end" }
                >
                  {
                    formik.values.waiverId > 0 && (
                          <Grid item xs={3} sx={{
                            justifyContent: "center",
                            alignItems: "center",
                            display: 'flex'
                          }}>
                            <Typography variant={"h4"}>
                              {`Waiver Id : ${formik.values.waiverId}`}
                            </Typography>
                          </Grid>
                      )
                  }
                  <Grid item xs={3}>
                    <Button
                        disabled={isDownloadButtonLoading}
                        fullWidth
                        onClick={async ()=>{

                          try{

                            setIsDownloadButtonLoading(true)

                            if(!formik.values.waiverId
                                || (selectedPatientWaiver.scholarshipPercentage === null || selectedPatientWaiver.fplRatio === null || selectedPatientWaiver.expenseIncomeRatio === null)
                            ){
                              dispatch(showErrorStatus("Run Waiver Eligibility and then save the form before downloading"))
                              return
                            }

                            const response = await patientService.exportPatientWaiver({
                              patientId: formik.values.patientId,
                              waiverId: formik.values.waiverId
                            })

                            if (response.hasErrors) {
                              dispatch(showErrorStatus("Error downloading waiver. Please reach out to a FinPay administrator."));
                              return
                            }

                            let blob = new Blob([response.entity.data], { type: "application/pdf" });

                            FileSaver.saveAs(blob, "patient-waiver.pdf");

                          }finally{
                            setIsDownloadButtonLoading(false)
                          }

                        }}
                        test-id="patient-tab-download-waiver-btn"
                    >
                      Download Waiver
                    </Button>
                  </Grid>
                </Grid>


              </Grid>
          ): (<LoadingOverlay/>)
        }
      </>
  )
}

export const buildClientScholarshipConfigObj = (scholarshipConfig : Scholarship[]) => {

  scholarshipConfig.sort((a: Scholarship, b: Scholarship) => b.scholarshipPercentage - a.scholarshipPercentage)

  let clientScholarshipConfig: ScholarshipConfig = {
    config: scholarshipConfig,
    fplSlider: {
      sliderMin: 0,
      sliderMax: Math.ceil(scholarshipConfig?.[scholarshipConfig.length-1]?.fplApprovalThreshold*1.1),
      ranges: [],
    },
    expenseIncomeSlider: {
      sliderMin: 0,
      sliderMax: Math.ceil(scholarshipConfig?.[0]?.incomeThreshold * 1.1),
      ranges: [],
    }
  }

  //Organizing fpl slider data
  //Scholarship percentage in desc order
  for(let i=0; i < scholarshipConfig.length; i++){

    if(i === scholarshipConfig.length -1){
      clientScholarshipConfig.fplSlider.ranges.push({
        minVal: scholarshipConfig?.[i-1]?.fplApprovalThreshold,
        maxVal: scholarshipConfig?.[i]?.fplApprovalThreshold,
        label:  'No Waiver'
      })
    }else{
      clientScholarshipConfig.fplSlider.ranges.push({
        minVal: i ===0 ? 0 : scholarshipConfig?.[i-1]?.fplApprovalThreshold,
        maxVal: scholarshipConfig?.[i]?.fplApprovalThreshold,
        label:  scholarshipConfig?.[i]?.scholarshipPercentage === 100 ?
            'Full Waiver' :
            scholarshipConfig?.[i]?.scholarshipPercentage > 0 && scholarshipConfig?.[i]?.scholarshipPercentage < 100 ? 'Partial Waiver' :
                'No Waiver'
      })
    }

  }

  //Organizing expense income ratio slider data
  //Scholarship percentage in asc order
  const reversedConfig = scholarshipConfig.reverse()

  for(let i=0; i < reversedConfig.length; i++){

    if(i ===0){
      clientScholarshipConfig.expenseIncomeSlider.ranges.push({
        minVal: 0,
        maxVal: reversedConfig?.[i]?.incomeThreshold,
        label: reversedConfig?.[i]?.scholarshipPercentage === 100 ?
            'Full Waiver' :
            scholarshipConfig?.[i]?.scholarshipPercentage > 0 && scholarshipConfig?.[i]?.scholarshipPercentage < 100 ? 'Partial Waiver' :
                'No Waiver'
      })
    }else{
      clientScholarshipConfig.expenseIncomeSlider.ranges.push({
        minVal: reversedConfig?.[i-1]?.incomeThreshold,
        maxVal: i === reversedConfig.length -1 ? reversedConfig?.[i]?.incomeThreshold : reversedConfig?.[i]?.incomeThreshold,
        label: reversedConfig?.[i]?.scholarshipPercentage === 100 ?
            'Full Waiver' :
            scholarshipConfig?.[i]?.scholarshipPercentage > 0 && scholarshipConfig?.[i]?.scholarshipPercentage < 100 ? 'Partial Waiver' :
                'No Waiver'
      })
    }

  }

  return clientScholarshipConfig;

}